import { useMemo, useState, type KeyboardEvent } from 'react';
import type { ProposalExtraItemType } from '@econtractor/types';
import type { ServiceSelectOption } from 'modules/product';
import { safeFloatParse } from 'utils/helpers/safeParsing';
import { sanitizeNumericInput } from 'utils/helpers/functions/sanitizeNumericInput';
import { getFmtRate } from '../../model/templatePriceUtils';

export type AdditionalSourceType = 'manual' | 'catalog';

export interface UseTemplateAdditionalItemsParams {
    items: ProposalExtraItemType[];
    rate: number;
    onChange: (items: ProposalExtraItemType[]) => void;
}

const useTemplateAdditionalItems = ({
    items,
    rate,
    onChange,
}: UseTemplateAdditionalItemsParams) => {
    const [sourceType, setSourceType] = useState<AdditionalSourceType>('catalog');
    const [selectedService, setSelectedService] = useState<ServiceSelectOption | null>(null);
    const [draftName, setDraftName] = useState('');
    const [draftCount, setDraftCount] = useState(1);
    const [draftPrice, setDraftPrice] = useState('');

    const fmtRate = useMemo(() => getFmtRate(rate), [rate]);
    const canAddManual = draftName.trim().length > 0 && draftCount > 0;
    const canAddCatalog = !!selectedService?.value && draftCount > 0;
    const canAdd = sourceType === 'manual' ? canAddManual : canAddCatalog;

    const resetDraft = () => {
        setDraftName('');
        setDraftCount(1);
        setDraftPrice('');
        setSelectedService(null);
    };

    const handleAddManual = () => {
        if (!canAddManual) return;
        const price = safeFloatParse(draftPrice || 0) || 0;
        const priceSale = price;
        onChange([
            ...items,
            {
                rowId: `${Date.now()}-${draftName}`,
                itemName: draftName.trim(),
                itemCount: Math.max(0, Math.floor(draftCount) || 0),
                price,
                priceRate: safeFloatParse(price * fmtRate) || 0,
                priceSale,
                priceSaleRate: safeFloatParse(priceSale * fmtRate) || 0,
            },
        ]);
        resetDraft();
    };

    const handleAddCatalog = () => {
        if (!selectedService?.value || draftCount <= 0) return;
        const price = safeFloatParse(selectedService.defPrice || 0) || 0;
        const priceSale = safeFloatParse(selectedService.defPriceSale || price) || 0;
        onChange([
            ...items,
            {
                rowId: `${Date.now()}-service-${selectedService.value}`,
                serviceId: selectedService.value,
                itemName: selectedService.label,
                itemCount: Math.max(0, Math.floor(draftCount) || 0),
                price,
                priceRate: safeFloatParse(price * fmtRate) || 0,
                priceSale,
                priceSaleRate: safeFloatParse(priceSale * fmtRate) || 0,
            },
        ]);
        resetDraft();
    };

    const handleAdd = () => {
        if (!canAdd) return;
        if (sourceType === 'manual') handleAddManual();
        else handleAddCatalog();
    };

    const handleDraftKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            handleAdd();
        }
    };

    const handleSourceChange = (next: AdditionalSourceType) => {
        setSourceType(next);
        setSelectedService(null);
        setDraftName('');
        setDraftPrice('');
        setDraftCount(1);
    };

    const setDraftCountDown = () => {
        setDraftCount((prev) => Math.max(1, Math.floor(prev) - 1));
    };

    const setDraftCountUp = () => {
        setDraftCount((prev) => Math.floor(prev) + 1);
    };

    const setDraftCountFromInput = (value: string) => {
        const parsed = parseInt(value, 10);
        setDraftCount(Number.isFinite(parsed) ? Math.max(0, parsed) : 0);
    };

    const setDraftPriceFromInput = (value: string) => {
        setDraftPrice(sanitizeNumericInput(value, 2));
    };

    return {
        sourceType,
        handleSourceChange,
        selectedService,
        setSelectedService,
        draftName,
        setDraftName,
        draftCount,
        setDraftCountDown,
        setDraftCountUp,
        setDraftCountFromInput,
        draftPrice,
        setDraftPriceFromInput,
        canAdd,
        handleAdd,
        handleDraftKeyDown,
    };
};

export default useTemplateAdditionalItems;
